/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { type FC } from "react";
import { useLocation, useNavigate } from "react-router";
import { theme } from "../styles/theme/theme";

interface TabDef {
  label: string;
  path: string;
}

const TABS: TabDef[] = [
  { label: "Planning Console", path: "/shipments" },
  { label: "Pivot", path: "/shipments/reporting" },
];

// Inline title-position tabs. Each tab is rendered at h1 typography so the
// active one reads like a page title; inactive ones sit alongside in muted
// foreground. Underline indicates the active tab without the full-width
// border-bottom strip the older design used.
const s = {
  wrap: css`
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
  `,
  tab: (active: boolean) => css`
    all: unset;
    flex-shrink: 0;
    cursor: pointer;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.4;
    color: ${active ? theme.colors.foreground : theme.colors.mutedForeground};
    border-bottom: 2px solid ${active ? theme.colors.foreground : "transparent"};
    padding-bottom: 2px;
    white-space: nowrap;
    transition: color 0.15s, border-color 0.15s;
    &:hover {
      color: ${theme.colors.foreground};
      border-bottom-color: ${active ? theme.colors.foreground : theme.colors.gray400};
    }
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px ${theme.colors.blue50};
      border-radius: 2px;
    }
  `,
};

const isActive = (current: string, target: string): boolean => {
  if (target === "/shipments") {
    return current === "/shipments" || current === "/shipments/planning-console";
  }
  return current === target || current.startsWith(target + "/");
};

export const ShipmentsTabs: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <div css={s.wrap} role="tablist" aria-label="Shipments views">
      {TABS.map((t) => {
        const active = isActive(pathname, t.path);
        return (
          <button
            key={t.path}
            type="button"
            role="tab"
            aria-selected={active}
            css={s.tab(active)}
            onClick={() => navigate(t.path)}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
};
